import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, FormControl, InputLabel, Select, MenuItem,
  Checkbox, FormControlLabel, Alert, Paper, Divider, Skeleton,
  useTheme, alpha,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { peopleService, Guardian } from '../../../services/peopleService';
import { AdmissionApplication, AdmissionCycle, OfferTerm } from '../../../types/admissions';
import { applicableAdmissionFeeHeads, concessionAmountLabel, concessionValue, optionalAdmissionFeeHeads, sumFeeHeads } from '../../../utils/admissionFees';
import { concessionTypeLabel } from './ConcessionRequestDialog';

interface Props {
  open: boolean;
  application: AdmissionApplication;
  cycle: AdmissionCycle;
  onCancel: () => void;
  onDone: () => Promise<void> | void;
}

/**
 * OfferAcceptanceDialog — the staff-mediated acceptance recording per
 * Journey 1 Step 12. Operator picks which Guardian is signing off, ticks
 * each cycle.offerTerms[] checkbox as the parent confirms verbally (or
 * uploads the signed scan — future enhancement), and submits.
 *
 * The backend `recordOfferAcceptance` auto-chains OfferAccepted → AdmFeePending
 * (if an admission fee applies to the applicant's class) or → Confirmed. The
 * dialog shows the exact challan — the class's heads plus any optional heads
 * the parent opts into — before the operator clicks.
 */

const OfferAcceptanceDialog: React.FC<Props> = ({ open, application, cycle, onCancel, onDone }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [guardians, setGuardians] = useState<Guardian[]>([]);
  const [loadingGuardians, setLoadingGuardians] = useState(true);
  const [selectedGuardianId, setSelectedGuardianId] = useState<string>('');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [optedIn, setOptedIn] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const familyId = typeof application.familyId === 'object'
    ? application.familyId._id
    : application.familyId;

  // Load the family's guardians so the operator can pick who's signing.
  useEffect(() => {
    if (!open || !familyId) return;
    let cancelled = false;
    (async () => {
      setLoadingGuardians(true);
      try {
        const family = await peopleService.getFamily(familyId as string);
        if (!cancelled) {
          setGuardians(family.guardians);
          // Default to the primary contact when known.
          const primaryId = family.family.primaryContactGuardianId;
          const defaultId = (typeof primaryId === 'object' ? primaryId?._id : primaryId)
            || family.guardians[0]?._id
            || '';
          setSelectedGuardianId(defaultId);
        }
      } catch (err: any) {
        if (!cancelled) {
          enqueueSnackbar(err?.message || 'Failed to load family guardians', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingGuardians(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, familyId, enqueueSnackbar]);

  const terms = useMemo(() => cycle.offerTerms || [], [cycle.offerTerms]);
  const requiredTermKeys = useMemo(() => terms.filter(t => t.required).map(t => t.key), [terms]);
  const allRequiredChecked = requiredTermKeys.every(k => checked.has(k));

  const toggleTerm = (key: string) => {
    const next = new Set(checked);
    if (next.has(key)) next.delete(key); else next.add(key);
    setChecked(next);
  };

  const classId = typeof application.classId === 'object' ? application.classId._id : application.classId;
  const optionalHeads = useMemo(
    () => optionalAdmissionFeeHeads(cycle.admissionFeeHeads, classId),
    [cycle.admissionFeeHeads, classId],
  );
  const feeHeads = useMemo(
    () => applicableAdmissionFeeHeads(cycle.admissionFeeHeads, classId, optedIn),
    [cycle.admissionFeeHeads, classId, optedIn],
  );
  const feeTotal = sumFeeHeads(feeHeads);
  // Approved one-time concessions come off the challan as waivers — same math as the server.
  const concessions = (application.concessionRequests ?? [])
    .filter(c => c.status === 'approved' && !c.carryToFees && !c.mintedFeeWaiverId)
    .map(c => ({ label: `${concessionTypeLabel(c.type)} ${concessionAmountLabel(c)}`, amount: concessionValue(c, feeTotal) }));
  const payable = Math.max(0, feeTotal - concessions.reduce((s, c) => s + c.amount, 0));
  const toggleOptional = (label: string) =>
    setOptedIn(prev => (prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]));

  const close = () => { if (!busy) onCancel(); };

  const submit = async () => {
    if (!selectedGuardianId) return;
    setBusy(true);
    try {
      await admissionApplicationService.recordOfferAcceptance(application._id, {
        onBehalfOfGuardianId: selectedGuardianId,
        termsAccepted: Array.from(checked).map(termKey => ({ termKey })),
        optionalHeadLabels: optedIn,
      });
      enqueueSnackbar('Offer acceptance recorded', { variant: 'success' });
      await onDone();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to record offer acceptance', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" fontWeight={700}>Record offer acceptance</Typography>
        <Typography variant="caption" color="text.secondary">
          You're recording this on behalf of the parent. The audit trail captures who signed off.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* Guardian picker */}
          {loadingGuardians ? (
            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1.5 }} />
          ) : guardians.length === 0 ? (
            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                No guardians attached to this family
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The Resolve Family step hasn't been completed yet — Guardian records are needed
                before an offer can be formally accepted.
              </Typography>
            </Alert>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel>On behalf of</InputLabel>
              <Select
                value={selectedGuardianId}
                label="On behalf of"
                onChange={(e) => setSelectedGuardianId(e.target.value)}
              >
                {guardians.map(g => (
                  <MenuItem key={g._id} value={g._id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{g.name}</Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'capitalize' }}>
                        · {g.relation}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">· {g.phone}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Terms */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, mb: 1, display: 'block' }}>
              Offer terms
            </Typography>
            {terms.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No terms set on this cycle. Acceptance is recorded as-is.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {terms.map((term: OfferTerm) => (
                  <FormControlLabel
                    key={term.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={checked.has(term.key)}
                        onChange={() => toggleTerm(term.key)}
                      />
                    }
                    label={
                      <Stack spacing={0.5} sx={{ py: 0.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>{term.label}</Typography>
                          {term.required && (
                            <Typography variant="caption" sx={{ color: theme.palette.warning.dark, fontWeight: 700, letterSpacing: 0.3 }}>
                              REQUIRED
                            </Typography>
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {term.content}
                        </Typography>
                      </Stack>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                ))}
              </Stack>
            )}
          </Paper>

          <Divider />

          {/* Admission fee — exactly what the challan will carry */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, mb: 1, display: 'block' }}>
              Admission fee
            </Typography>
            {feeHeads.length === 0 && optionalHeads.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No admission fee applies to this class — the seat is confirmed straight away.
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {feeHeads.filter(h => !h.optional).map(h => (
                  <Stack key={h.label} direction="row" justifyContent="space-between">
                    <Typography variant="body2">{h.label}</Typography>
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{h.amount.toLocaleString()}</Typography>
                  </Stack>
                ))}
                {optionalHeads.map(h => (
                  <Stack key={h.label} direction="row" justifyContent="space-between" alignItems="center">
                    <FormControlLabel
                      sx={{ m: 0, ml: -0.75 }}
                      control={<Checkbox size="small" checked={optedIn.includes(h.label)} onChange={() => toggleOptional(h.label)} />}
                      label={<Typography variant="body2">{h.label} <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography></Typography>}
                    />
                    <Typography variant="body2" color={optedIn.includes(h.label) ? 'text.primary' : 'text.disabled'} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {h.amount.toLocaleString()}
                    </Typography>
                  </Stack>
                ))}
                {concessions.map(c => (
                  <Stack key={c.label} direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Less: {c.label}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>−{c.amount.toLocaleString()}</Typography>
                  </Stack>
                ))}
                <Divider sx={{ my: 0.5 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={700}>{concessions.length ? 'Payable' : 'Total'}</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{payable.toLocaleString()}</Typography>
                </Stack>
              </Stack>
            )}
          </Paper>

          <Alert
            severity="info"
            variant="outlined"
            sx={{ borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.03) }}
          >
            <Typography variant="body2" fontWeight={600}>
              Next: {payable > 0 ? 'Admission fee pending' : 'Confirmed'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {feeTotal > 0
                ? `An admission-fee challan for ${feeTotal.toLocaleString()} is issued${concessions.length ? ` with the concession${concessions.length === 1 ? '' : 's'} taken off, leaving ${payable.toLocaleString()} to pay` : ''}; the seat is confirmed as soon as it is paid.`
                : 'Nothing to pay, so the application moves straight to Confirmed.'}
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          disableElevation
          onClick={submit}
          disabled={busy || !selectedGuardianId || !allRequiredChecked}
        >
          {busy ? 'Recording…' : 'Record acceptance'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfferAcceptanceDialog;
